// A type's level controls in the Types list (spec item 6 "Setting interest from the list"): the ★ makes it an
// interest; long-press (or the "…" that shows on hover / focus) offers Hide or Clear. Synced with the profile.
// The list (slice 1) mounts <TypeRowControls tag> in each row (the row element carries `data-type-row`, which reveals
// the "…") and puts `useTypeLevelMenu(tag)`'s press handlers on the row's toggle button.
import { requireAccount } from "../../profile/account";
import { useCallback, useEffect, useRef, useState } from "react";
import { T } from "../../shared/config";
import { Icon, type IconName } from "../../shared/icons";
import { normTag } from "../../profile/model";
import { getProfile, useProfileView, type TagLevel } from "../../state/profile";
import styles from "./profile.module.css";

/** Which type's level menu is open (one at a time, across the list), so the list and its rows agree. */
let openTag: string | null = null;
const listeners = new Set<() => void>();
const setOpenTag = (t: string | null) => { openTag = t; listeners.forEach((fn) => fn()); };
const useOpenTag = () => {
  const [, bump] = useState(0);
  useEffect(() => { const fn = () => bump((n) => n + 1); listeners.add(fn); return () => { listeners.delete(fn); }; }, []);
  return openTag;
};

/** Closes the level menu if one is open (true when it did): Escape's first stop in the Types list. */
export function closeLevelMenu(): boolean {
  if (!openTag) return false;
  setOpenTag(null);
  return true;
}

/** Long-press (touch or mouse) on a row opens its level menu, like the phone. Spread onto the row's toggle button;
 *  `pressed()` says the press just opened it, so the click that follows shouldn't also toggle the filter. */
export function useTypeLevelPress(tag: string) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fired = useRef(false);
  const cancel = useCallback(() => clearTimeout(timer.current), []);
  useEffect(() => cancel, [cancel]);
  return {
    pressed: () => { const f = fired.current; fired.current = false; return f; },
    handlers: {
      onPointerDown: () => { fired.current = false; timer.current = setTimeout(() => { fired.current = true; requireAccount("typeLevels", () => setOpenTag(openTag === tag ? null : tag)); }, 500); },
      onPointerUp: cancel,
      onPointerLeave: cancel,
      onContextMenu: (ev: React.MouseEvent) => ev.preventDefault(),
    },
  };
}

/** The ★, the "…" and the menu for one type. Renders as the row's trailing grid cells. */
export function TypeRowControls({ tag }: { tag: string }) {
  const level = useProfileView().levels.get(normTag(tag)) ?? null;
  const open = useOpenTag() === tag;
  const profile = getProfile();
  const firstItem = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (open) firstItem.current?.focus(); }, [open]);
  const items: [TagLevel | null, IconName, "level.hide" | "level.clear"][] = [];
  if (level !== "hidden") items.push(["hidden", "eye-off", "level.hide"]);
  if (level) items.push([null, "x", "level.clear"]);
  return (
    <>
      <button type="button" className={styles["type-star"]} aria-pressed={level === "interest"} data-star-type={tag}
        aria-label={`${T("level.interest")}: ${tag}`} onClick={() => requireAccount("typeLevels", () => profile.setTagLevel(tag, level === "interest" ? null : "interest"))}>
        <Icon name={level === "interest" ? "star-filled" : "star"} />
      </button>
      <button type="button" className={`${styles["icon-btn"]} ${styles["type-more"]}`} aria-label={T("level.menu", { tag })} aria-haspopup="menu"
        aria-expanded={open} onClick={() => requireAccount("typeLevels", () => setOpenTag(open ? null : tag))}><Icon name="dots" /></button>
      {open && (
        <div className={styles["level-menu"]} role="menu" aria-label={T("level.menu", { tag })}>
          {items.map(([l, ic, key], i) => (
            <button key={key} ref={i === 0 ? firstItem : undefined} type="button" role="menuitem" className={styles["level-item"]} data-level={l ?? "clear"}
              onClick={() => { setOpenTag(null); requireAccount("typeLevels", () => profile.setTagLevel(tag, l)); }}><Icon name={ic} />{T(key)}</button>
          ))}
        </div>
      )}
    </>
  );
}
